import { useMemo } from 'react';

import { MODULE, PageConfigUpdate } from '@gearbelt/global';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getWorkspacePageConfigQueryOptions, pageConfigKeys } from '@/queries/page-config';
import { ApiService } from '@/services/api-v2';
import { defaultActiveColumns, useModulesStore } from '@/stores/modules.store';
import { useUserStore } from '@/stores/user.store';

/**
 * Configuration for self-healing page config validation.
 * Provides the necessary metadata to validate filters, sorting, and grouping against available columns.
 */
export interface PageConfigTableMeta {
  /** Map of filter IDs to their corresponding API field names */
  filterFieldMap?: Record<string, string>;
  /** Available grouping options */
  groupOptions?: Array<{ accessor: string; label: string }>;
  /** Column definitions with id or accessorKey */
  columns?: Array<{ id?: string; accessorKey?: string }>;
}

/**
 * Validates and sanitizes filters to ensure only columns that exist in table-meta are used.
 * This prevents errors when columns are removed from the codebase but still exist in saved page_config.
 */
function sanitizeFilters(
  filters: Array<{ id: string; value: unknown }> | undefined,
  filterFieldMap?: Record<string, string>,
  columns?: Array<{ id?: string; accessorKey?: string }>,
): Array<{ id: string; value: unknown }> {
  if (!filters || !Array.isArray(filters)) return [];

  const validFilterIds = filterFieldMap ? Object.keys(filterFieldMap) : [];
  const validColumnIds = columns ? new Set(columns.map((col) => col.id || col.accessorKey).filter((id): id is string => id !== undefined)) : new Set<string>();

  return filters.filter((filter) => validFilterIds.includes(filter.id) || validColumnIds.has(filter.id));
}

/**
 * Validates and sanitizes sort order to ensure only columns that exist in table-meta are used.
 * This prevents errors when columns are removed from the codebase but still exist in saved page_config.
 */
function sanitizeSorting(
  sorting: { key?: string; direction?: 'asc' | 'desc' },
  columns?: Array<{ id?: string; accessorKey?: string }>,
): { key?: string; direction?: 'asc' | 'desc' } {
  if (!sorting || !sorting.key || !columns) return sorting || {};

  // Get all valid column IDs from the columns definition
  const validColumnIds = new Set(columns.map((col) => col.id || col.accessorKey).filter((id): id is string => id !== undefined));

  // Only return sorting if the key is valid
  if (validColumnIds.has(sorting.key)) {
    return sorting;
  }

  return {};
}

/**
 * Validates and sanitizes grouping to ensure only columns that exist in table-meta are used.
 * This prevents errors when columns are removed from the codebase but still exist in saved page_config.
 */
function sanitizeGrouping(grouping: string[] | undefined, groupOptions?: Array<{ accessor: string; label: string }>): string[] {
  if (!grouping || !Array.isArray(grouping) || !groupOptions) return grouping || [];

  const validGroupIds = groupOptions.filter((opt) => opt.accessor !== '').map((opt) => opt.accessor);

  return grouping.filter((group) => validGroupIds.includes(group));
}

/**
 * Validates and sanitizes column order to ensure only columns that exist in table-meta are used.
 * Use table-meta columns as source of truth, validate and order against page_configs
 * This prevents errors when columns are removed from the codebase but still exist in saved page_config.
 * Columns not yet in the saved order are inserted after their nearest preceding meta sibling, so a
 * newly shipped column appears where table-meta places it instead of at the end of the table.
 * A newly shipped column inherits its default visibility from `defaultActiveColumnIds` (the module's
 * `defaultActiveColumns` list) — absent from the list means hidden by default, matching every other
 * default-hidden column. When the list is omitted, new columns default to visible.
 */
export function sanitizeColumnOrder(
  columnOrder: Array<{ id: string; active: boolean }> | undefined,
  columns?: Array<{ id?: string; accessorKey?: string }>,
  defaultActiveColumnIds?: string[],
): Array<{ id: string; active: boolean }> {
  const apiColumnOrder = columnOrder || [];

  if (!columns || !Array.isArray(columns)) return apiColumnOrder;

  const metaColumnIds = columns
    .map((col) => {
      const columnId = col.id || col.accessorKey;
      return columnId ? String(columnId) : null;
    })
    .filter((id) => id !== null);

  const isActiveByDefault = (id: string) => (defaultActiveColumnIds ? defaultActiveColumnIds.includes(id) : true);

  const apiColumnMap = new Map<string, { id: string; active: boolean }>(apiColumnOrder.map((col) => [col.id, col]));

  const newTableMetaColumns = metaColumnIds.filter((id) => !apiColumnMap.has(id)).map((id) => ({ id, active: isActiveByDefault(id) }));

  const PINNED_FIRST_COLUMNS = ['select'];

  const orderedColumns = apiColumnOrder.filter((col) => metaColumnIds.includes(col.id) && !PINNED_FIRST_COLUMNS.includes(col.id));

  for (const newColumn of newTableMetaColumns.filter((col) => !PINNED_FIRST_COLUMNS.includes(col.id))) {
    const precedingMetaIds = metaColumnIds.slice(0, metaColumnIds.indexOf(newColumn.id)).reverse();
    const anchorId = precedingMetaIds.find((id) => orderedColumns.some((col) => col.id === id));
    const anchorIndex = anchorId ? orderedColumns.findIndex((col) => col.id === anchorId) : -1;
    orderedColumns.splice(anchorIndex + 1, 0, newColumn);
  }

  const pinnedColumns = PINNED_FIRST_COLUMNS.filter((id) => metaColumnIds.includes(id)).map((id) => ({
    id,
    active: apiColumnMap.get(id)?.active ?? true,
  }));

  return [...pinnedColumns, ...orderedColumns];
}

export function usePageConfig(handle: MODULE, tableMeta?: PageConfigTableMeta) {
  const queryClient = useQueryClient();

  const { org: workspace } = useUserStore();
  const { getModule } = useModulesStore();

  const { data: pageConfigData, isLoading: isLoadingPageConfig } = useQuery({
    ...getWorkspacePageConfigQueryOptions({ workspaceId: workspace!.id, handle }),
    enabled: !!workspace?.id,
  });

  const { mutateAsync: updatePageConfigMutation } = useMutation({
    mutationFn: async (moduleUpdate: PageConfigUpdate) => {
      if (!workspace) {
        throw new Error('Workspace is not defined');
      }

      return (
        await ApiService.UpdateWorkspacePageConfig({
          workspaceId: workspace.id,
          handle,
          table: {
            ...pageConfigData?.table,
            ...moduleUpdate.table,
          },
          view: moduleUpdate.view || pageConfigData?.view,
        })
      ).json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pageConfigKeys.byHandle(workspace?.id, handle) });
    },
  });

  const apiTableSetting = pageConfigData?.table;
  const apiViewSetting = pageConfigData?.view;
  const { table: storageTableSetting } = getModule(handle);

  const rawFilters = storageTableSetting.filters;

  const rawSorting = useMemo(() => {
    return apiTableSetting?.sorting || {};
  }, [apiTableSetting?.sorting]);

  const rawGrouping = useMemo(() => {
    return apiTableSetting?.grouping || [];
  }, [apiTableSetting?.grouping]);

  const rawColumnOrder = useMemo(() => {
    return apiTableSetting?.columnOrder || [];
  }, [apiTableSetting?.columnOrder]);

  // Sanitize page config values to prevent errors from removed columns
  const filters = useMemo(
    () => sanitizeFilters(rawFilters, tableMeta?.filterFieldMap, tableMeta?.columns),
    [rawFilters, tableMeta?.filterFieldMap, tableMeta?.columns],
  );

  const sorting = useMemo(() => sanitizeSorting(rawSorting, tableMeta?.columns), [rawSorting, tableMeta?.columns]);

  const grouping = useMemo(() => sanitizeGrouping(rawGrouping, tableMeta?.groupOptions), [rawGrouping, tableMeta?.groupOptions]);

  const columnOrder = useMemo(
    () => sanitizeColumnOrder(rawColumnOrder, tableMeta?.columns, defaultActiveColumns[handle]),
    [rawColumnOrder, tableMeta?.columns, handle],
  );

  const columnVisibility = useMemo(() => {
    return columnOrder.reduce(
      (acc, col) => {
        acc[col.id] = col.active;
        return acc;
      },
      {} as Record<string, boolean>,
    );
  }, [columnOrder]);

  const view = useMemo(() => {
    return apiViewSetting || '';
  }, [apiViewSetting]);

  function updatePageConfig(data: PageConfigUpdate) {
    updatePageConfigMutation(data);
  }

  return {
    loading: isLoadingPageConfig,
    config: {
      filters: filters ?? [],
      sorting: sorting ?? {},
      grouping: grouping ?? [],
      columnOrder: columnOrder ?? [],
      columnVisibility: columnVisibility ?? {},
      view: view || 'grid',
    },
    updatePageConfig,
  };
}
